import React from 'react'

import {useFormik} from 'formik'
import toString from 'lodash/toString'
import {useNavigate} from 'react-router'
import * as Yup from 'yup'

import * as providerInfoApi from '../../services/api/provider-info'
import {goToEditProviderInfo} from '../../services/navigation'
import {
  PossiblyMixedValue,
  isMixedValue,
  toMixedValidationSchema,
  toPossiblyMixedValue,
  toTouchedFormData,
  unlockMixedValuesLabel,
} from '../utils/form'

import {EditProviderInfoTab, ProviderInfo} from '../../types'

import closeIcon from '../../../../commons/src/assets/icons/close.svg'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'

import SaveButtonPartial from '../save-button-partial'

type Props = {
  providerInfos: Array<ProviderInfo>
}

type SingleFormData = {
  materialName: string
}

type MixedFormData = {
  materialName: PossiblyMixedValue<string>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const initialValues = {
  materialName: '',
}

const validationSchema = {
  materialName: Yup.string().label('Material name'),
}

const productionTimesToFormData = (providerInfos: ProviderInfo[]): MixedFormData => ({
  materialName: toPossiblyMixedValue(
    providerInfos.map(providerInfo => providerInfo.materialName),
    toString
  ),
})

const EditMaterialNamePartial = ({providerInfos}: Props) => {
  const navigate = useNavigate()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...productionTimesToFormData(providerInfos),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const providerInfoRequest = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        const nextProviderInfos = await providerInfoApi.patchMultiple(
          providerInfoRequest,
          providerInfoIds
        )

        goToEditProviderInfo(navigate, {
          providerInfos: nextProviderInfos,
          activeTab: EditProviderInfoTab.MATERIAL_NAME,
          replace: true,
        })

        setIsSubmitted(true)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Paragraph>
        The material name below will be used in the order confirmation emails you receive from us
        and is not visible to your customers. You can use the name field to specify your own
        internal material brand names.
      </Paragraph>
      <Stack space="300">
        <FormRow>
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.materialName}>
            <InputField
              placeholder="Name (optional)"
              name="materialName"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.materialName)}
              value={
                isMixedValue(formik.values.materialName)
                  ? initialValues.materialName
                  : formik.values.materialName
              }
              appearance={
                formik.submitCount > 0 && !!formik.errors.materialName ? 'error' : undefined
              }
              onChange={(value, name, event) => formik.handleChange(event)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.materialName) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() => formik.setFieldValue('materialName', initialValues.materialName)}
                  />
                ) : undefined
              }
            />
          </FieldNote>
        </FormRow>
        <SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />
      </Stack>
    </form>
  )
}

export default EditMaterialNamePartial
